import { create } from 'zustand';
import { DEFAULT_USER_SETTINGS } from '@en3/shared';

/**
 * 阅读器的排版设置。
 *
 * 为什么用 zustand + localStorage 而不是放进用户设置（后端 `users.settings_json`）：
 * 1. **字号/行距是"当下就想调"的东西**：调一次要发一个 PATCH 请求、等往返，
 *    手感很差；本地存就立刻生效。
 * 2. 未登录用户也应该能调（阅读免登录）。
 * 因此本地是"立即生效层"，登录用户的偏好同步留到个人中心里去改，
 * 两者不冲突：本地的值优先级更高（用户在这个设备上的选择）。
 *
 * 主题（明/暗）仍然由 `theme.store` 管，不在这里重复。
 */

export interface ReaderSettings {
  fontSize: number;
  lineHeight: number;
  /** 正文最大宽度（px）。窄一点更接近纸质书，宽一点适合长句 */
  maxWidth: number;
  /** 是否高亮长难句 */
  highlightLong: boolean;
  /** 点词后是否自动把词加入生词本（默认关：不要替用户做决定） */
  autoCollect: boolean;
}

const STORAGE_KEY = 'en3.reader.settings';

export const READER_LIMITS = {
  fontSize: { min: 15, max: 26, step: 1 },
  lineHeight: { min: 1.4, max: 2.4, step: 0.1 },
  maxWidth: { min: 34, max: 60, step: 2 },
} as const;

const DEFAULTS: ReaderSettings = {
  fontSize: DEFAULT_USER_SETTINGS.readerFontSize,
  lineHeight: DEFAULT_USER_SETTINGS.readerLineHeight,
  maxWidth: 42,
  highlightLong: true,
  autoCollect: false,
};

function clamp(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value));
}

/** 读取本地设置。任何异常都退回默认值（隐私模式下 localStorage 会抛错）。 */
export function loadReaderSettings(): ReaderSettings {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return DEFAULTS;
    const parsed = JSON.parse(raw) as Partial<ReaderSettings>;
    return {
      fontSize: clamp(Number(parsed.fontSize ?? DEFAULTS.fontSize), READER_LIMITS.fontSize.min, READER_LIMITS.fontSize.max),
      lineHeight: clamp(
        Number(parsed.lineHeight ?? DEFAULTS.lineHeight),
        READER_LIMITS.lineHeight.min,
        READER_LIMITS.lineHeight.max,
      ),
      maxWidth: clamp(Number(parsed.maxWidth ?? DEFAULTS.maxWidth), READER_LIMITS.maxWidth.min, READER_LIMITS.maxWidth.max),
      highlightLong: parsed.highlightLong ?? DEFAULTS.highlightLong,
      autoCollect: parsed.autoCollect ?? DEFAULTS.autoCollect,
    };
  } catch {
    return DEFAULTS;
  }
}

/**
 * 把设置写到 CSS 变量上。
 *
 * 用 CSS 变量而不是内联样式，是因为 `tailwind.css` 里的 `reader-prose` 工具类
 * 已经读这三个变量（见 `--reader-font-size` 等）。这样"改设置"只是改变量，
 * 不触发 React 重渲染整篇正文 —— 长文 + 逐词 span 的 DOM 很重，
 * 每次调字号都重渲染会有明显卡顿。
 */
export function applyReaderSettings(settings: ReaderSettings): void {
  if (typeof document === 'undefined') return;
  const root = document.documentElement;
  root.style.setProperty('--reader-font-size', `${settings.fontSize}px`);
  root.style.setProperty('--reader-line-height', String(settings.lineHeight));
  root.style.setProperty('--reader-max-width', `${settings.maxWidth}rem`);
}

interface ReaderState extends ReaderSettings {
  update: (patch: Partial<ReaderSettings>) => void;
  reset: () => void;
}

export const useReaderSettings = create<ReaderState>()((set, get) => ({
  ...DEFAULTS,
  update: (patch) => {
    const next = { ...get(), ...patch };
    const settings: ReaderSettings = {
      fontSize: clamp(next.fontSize, READER_LIMITS.fontSize.min, READER_LIMITS.fontSize.max),
      lineHeight: clamp(next.lineHeight, READER_LIMITS.lineHeight.min, READER_LIMITS.lineHeight.max),
      maxWidth: clamp(next.maxWidth, READER_LIMITS.maxWidth.min, READER_LIMITS.maxWidth.max),
      highlightLong: next.highlightLong,
      autoCollect: next.autoCollect,
    };
    applyReaderSettings(settings);
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(settings));
    } catch {
      // 存不进去也要在当前会话生效
    }
    set(settings);
  },
  reset: () => {
    applyReaderSettings(DEFAULTS);
    try {
      localStorage.removeItem(STORAGE_KEY);
    } catch {
      // 同上
    }
    set(DEFAULTS);
  },
}));

/** 启动时调用一次：把本地设置灌进 CSS 变量（避免首屏用默认字号闪一下）。 */
export function bootstrapReaderSettings(): void {
  applyReaderSettings(loadReaderSettings());
  useReaderSettings.setState(loadReaderSettings());
}
